import { useState, useEffect, useMemo, useRef } from 'react';
import type {
  FC,
  ReactNode,
  CSSProperties,
  MouseEvent,
  FormEvent,
  ChangeEvent,
} from 'react';

import { FaInstagram, FaWhatsapp } from 'react-icons/fa';

import Head from 'next/head';
import { NextPage } from 'next';

import styled, { createGlobalStyle } from 'styled-components';
import {
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion';
import { getVideoEmbedUrl } from '~/modules/trainings/utils/get-video-embed-url';
import { flattenTrainingLessons } from '~/modules/trainings/utils/flatten-training-lessons';
import { trainingModules } from '~/modules/trainings/data';

import { VMCORE_BRAND } from '~/common/constants/vmcore-brand';
import { VmcoreLogo } from '~/common/components/VmcoreLogo';
import { Collapse } from '~/common/components/Collapse';

const WHATSAPP_DESTINATION = '5519971621023';

// O tour em vídeo da landing consome a mesma trilha do módulo interno de
// Treinamentos: preencher os `videoUrl` no JSON acende os dois lugares.

/* ─── Global Styles ─────────────────────────────────────────────────── */
const LandingGlobal = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  :root {
    --azul-escuro:        #081A37;
    --azul-primario:      #0056ED;
    --azul-claro:         #0056ED;
    --azul-neon:          #0056ED;
    --branco:             #FFFFFF;
    --cinza-claro:        #E8F1FD;
    --cinza-medio:        #8896A6;
    --cinza-escuro:       #405471;
    --preto-suave:        #081A37;
    --verde-sucesso:      #147D46;
    --amarelo-alerta:     #F59E0B;
    --laranja-engenharia: #E8590C;

    /* Superfícies claras do KV, com azul-marinho para contraste. */
    --bg-0:               #E8F1FD;
    --bg-1:               #FFFFFF;
    --bg-2:               #F1F6FE;
    --linha:              rgba(67, 152, 241, 0.14);
    --texto-hi:           #081A37;
    --texto-mid:          rgba(48, 70, 104, 0.78);
    --texto-dim:          #526582;

    --gradiente-hero:     linear-gradient(135deg, #081A37 0%, #0F3460 50%, #0056ED 100%);
    --gradiente-cta:      linear-gradient(135deg, #0056ED 0%, #0045BF 100%);
    --font-principal:     'Plus Jakarta Sans', sans-serif;
    --font-mono:          'JetBrains Mono', monospace;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html { scroll-behavior: smooth; }

  body {
    font-family: var(--font-principal);
    color: var(--texto-mid);
    background: var(--bg-0);
    -webkit-font-smoothing: antialiased;
  }

  ::selection {
    background: rgba(0, 86, 237, 0.55);
    color: #fff;
  }

  ::-webkit-scrollbar { width: 10px; }
  ::-webkit-scrollbar-track { background: var(--bg-0); }
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 86, 237, 0.35);
    border-radius: 8px;
    border: 2px solid var(--bg-0);
  }
  ::-webkit-scrollbar-thumb:hover { background: rgba(67, 152, 241, 0.55); }

  /* Compensa o header fixo (64px) ao rolar até uma âncora. */
  [id] {
    scroll-margin-top: 64px;
  }

  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    *, *::before, *::after { animation: none !important; }
  }
`;

/* ─── Header ─────────────────────────────────────────────────────────── */
const Header = styled.header<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 48px;
  transition: background 0.3s, box-shadow 0.3s, border-color 0.3s;
  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(232, 241, 253, 0.94)' : 'rgba(232, 241, 253, 0.8)'};
  border-bottom: 1px solid
    ${({ $scrolled }) =>
      $scrolled ? 'rgba(67, 152, 241, 0.16)' : 'rgba(67, 152, 241, 0.06)'};
  backdrop-filter: blur(14px);
  box-shadow: ${({ $scrolled }) =>
    $scrolled ? '0 8px 32px rgba(8, 26, 55, 0.08)' : 'none'};

  @media (max-width: 767px) {
    padding: 0 20px;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  gap: 32px;
  align-items: center;

  & > button:not(:last-child) {
    position: relative;
    color: rgba(48, 70, 104, 0.8);
    background: none;
    border: none;
    font-family: var(--font-principal);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    padding: 4px 0;
    transition: color 0.2s;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -2px;
      height: 2px;
      width: 0%;
      border-radius: 2px;
      background: var(--gradiente-cta);
      box-shadow: 0 0 10px rgba(67, 152, 241, 0.8);
      transition: width 0.25s ease;
    }

    &:hover {
      color: var(--azul-primario);
      &::after {
        width: 100%;
      }
    }
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const HeaderCta = styled.button`
  font-family: var(--font-principal);
  cursor: pointer;
  border: none;
  background: var(--gradiente-cta);
  color: #fff !important;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  white-space: nowrap;
  box-shadow: 0 4px 18px rgba(0, 86, 237, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 26px rgba(0, 86, 237, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.22);
  }
  &:focus-visible {
    outline: 2px solid var(--azul-claro);
    outline-offset: 2px;
  }
`;

/* ─── Hero ───────────────────────────────────────────────────────────── */
const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: radial-gradient(
      ellipse at 80% 35%,
      rgba(67, 152, 241, 0.12),
      transparent 60%
    ),
    var(--bg-0);
  padding: 120px 64px 96px;
  position: relative;
  overflow: hidden;

  @media (max-width: 1023px) {
    padding: 110px 40px 80px;
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 767px) {
    padding: 104px 24px 64px;
  }
`;

/* Linha de horizonte energizada acima do piso */
const GridHorizon = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 33%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(67, 152, 241, 0.45) 30%,
    rgba(0, 86, 237, 0.7) 50%,
    rgba(67, 152, 241, 0.45) 70%,
    transparent
  );
  filter: blur(0.4px);
  box-shadow: 0 0 24px 2px rgba(67, 152, 241, 0.35);
  pointer-events: none;
  opacity: 0.12;

  @media (max-width: 1023px) {
    display: none;
  }
`;

/* Massas de luz que derivam lentamente ao fundo */
const Aurora = styled.div<{ $variant: 1 | 2 }>`
  position: absolute;
  width: 620px;
  height: 620px;
  border-radius: 50%;
  filter: blur(110px);
  pointer-events: none;
  opacity: 0.12;
  background: ${({ $variant }) =>
    $variant === 1
      ? 'radial-gradient(circle, rgba(0, 86, 237,0.55) 0%, transparent 65%)'
      : 'radial-gradient(circle, rgba(10, 60, 130,0.6) 0%, transparent 65%)'};
  ${({ $variant }) =>
    $variant === 1
      ? 'top: -180px; right: -120px; animation: auroraDrift 16s ease-in-out infinite alternate;'
      : 'bottom: -220px; left: -160px; animation: auroraDrift 22s ease-in-out infinite alternate-reverse;'}

  @keyframes auroraDrift {
    from {
      transform: translate(0, 0) scale(1);
    }
    to {
      transform: translate(-60px, 50px) scale(1.15);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  max-width: 590px;

  @media (max-width: 1023px) {
    max-width: 640px;
  }
`;

const HeroVisual = styled(motion.div)`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-left: 48px;

  @media (max-width: 1023px) {
    justify-content: center;
    padding-left: 0;
    margin-top: 48px;
    width: 100%;
  }

  @media (max-width: 599px) {
    display: none;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid rgba(67, 152, 241, 0.35);
  background: rgba(0, 86, 237, 0.12);
  color: var(--azul-neon);
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 28px;

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--azul-primario);
    animation: dotPulse 2.2s ease-out infinite;
    flex-shrink: 0;
  }

  @keyframes dotPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 86, 237, 0.5);
    }
    70% {
      box-shadow: 0 0 0 9px rgba(0, 86, 237, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 86, 237, 0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .dot {
      animation: none;
    }
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(38px, 4.8vw, 64px);
  font-weight: 800;
  line-height: 1.06;
  letter-spacing: -0.025em;
  color: var(--texto-hi);
  margin-bottom: 24px;

  em {
    font-style: normal;
    background: linear-gradient(100deg, #0056ed 0%, #0045bf 50%, #0056ed 100%);
    background-size: 220% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmerText 6s linear infinite;
  }

  @keyframes shimmerText {
    from {
      background-position: 0% center;
    }
    to {
      background-position: 220% center;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    em {
      animation: none;
    }
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(15px, 1.6vw, 18px);
  line-height: 1.75;
  color: var(--texto-mid);
  max-width: 500px;
  margin-bottom: 40px;

  @media (max-width: 1023px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const HeroCtas = styled(motion.div)`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 1023px) {
    justify-content: center;
  }
`;

const BtnPrimary = styled.button`
  position: relative;
  overflow: hidden;
  font-family: var(--font-principal);
  cursor: pointer;
  border: none;
  background: var(--gradiente-cta);
  color: #fff;
  padding: 15px 34px;
  border-radius: 12px;
  font-size: 15.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 8px 32px rgba(0, 86, 237, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);

  /* brilho varrendo o botão periodicamente */
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(
      105deg,
      transparent 42%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 58%
    );
    transform: translateX(-130%);
    animation: btnSweep 4.4s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes btnSweep {
    0%,
    58% {
      transform: translateX(-130%);
    }
    78%,
    100% {
      transform: translateX(130%);
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 44px rgba(0, 86, 237, 0.6),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }
  &:focus-visible {
    outline: 2px solid var(--azul-claro);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const BtnSecondary = styled.button`
  font-family: var(--font-principal);
  cursor: pointer;
  background: rgba(255, 255, 255, 0.03);
  color: rgba(8, 26, 55, 0.9);
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 15.5px;
  font-weight: 600;
  border: 1.5px solid rgba(67, 152, 241, 0.3);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: border-color 0.2s, color 0.2s, background 0.2s, box-shadow 0.2s;
  &:hover {
    border-color: rgba(0, 86, 237, 0.7);
    color: var(--azul-primario);
    background: rgba(0, 86, 237, 0.12);
    box-shadow: 0 0 24px rgba(0, 86, 237, 0.25);
  }
  &:focus-visible {
    outline: 2px solid var(--azul-claro);
    outline-offset: 3px;
  }
`;

const HeroStats = styled(motion.div)`
  display: flex;
  margin-top: 56px;
  flex-wrap: wrap;
  row-gap: 20px;

  .stat {
    padding: 2px 28px;
    border-left: 1px solid rgba(67, 152, 241, 0.22);
    display: flex;
    flex-direction: column;
    gap: 5px;

    &:first-child {
      padding-left: 0;
      border-left: none;
    }
  }

  strong {
    font-family: var(--font-mono);
    font-size: 20px;
    font-weight: 500;
    color: var(--texto-hi);
    letter-spacing: -0.02em;

    em {
      font-style: normal;
      color: var(--azul-claro);
    }
  }

  span {
    font-size: 12.5px;
    color: var(--texto-dim);
    letter-spacing: 0.02em;
  }

  @media (max-width: 1023px) {
    justify-content: center;
  }

  @media (max-width: 599px) {
    .stat {
      padding: 2px 16px;
    }
  }
`;

const ScrollCue = styled.div`
  position: absolute;
  left: 50%;
  bottom: 26px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  z-index: 2;
  opacity: 0.6;
  pointer-events: none;

  .mouse {
    width: 22px;
    height: 34px;
    border: 1.5px solid rgba(64, 84, 113, 0.7);
    border-radius: 12px;
    display: flex;
    justify-content: center;
    padding-top: 6px;
  }

  .mouse span {
    width: 3px;
    height: 7px;
    border-radius: 2px;
    background: var(--azul-claro);
    animation: cueDrop 1.9s ease-in-out infinite;
  }

  p {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--texto-dim);
  }

  @keyframes cueDrop {
    0% {
      transform: translateY(0);
      opacity: 1;
    }
    65% {
      transform: translateY(11px);
      opacity: 0;
    }
    66% {
      transform: translateY(0);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 1023px) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .mouse span {
      animation: none;
    }
  }
`;

/* ─── Ticker de módulos ──────────────────────────────────────────────── */
const TickerStrip = styled.div`
  position: relative;
  overflow: hidden;
  padding: 20px 0;
  background: var(--azul-primario);
  border-top: 1px solid var(--linha);
  border-bottom: 1px solid var(--linha);

  /* máscaras de fade nas bordas */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 120px;
    z-index: 1;
    pointer-events: none;
  }
  &::before {
    left: 0;
    background: linear-gradient(90deg, var(--azul-primario), transparent);
  }
  &::after {
    right: 0;
    background: linear-gradient(-90deg, var(--azul-primario), transparent);
  }
`;

const TickerTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 56px;
  width: max-content;
  animation: tickerScroll 38s linear infinite;

  &:hover {
    animation-play-state: paused;
  }

  @keyframes tickerScroll {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-50%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TickerItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 56px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #ffffff;
  white-space: nowrap;

  svg {
    color: #e8f1fd;
    flex-shrink: 0;
  }
`;

/* ─── Section wrappers ───────────────────────────────────────────────── */
const Section = styled.section<{ $bg?: string }>`
  padding: 104px 48px;
  background: ${({ $bg }) => $bg || 'var(--bg-1)'};
  &[id='diferenciais'] {
    --texto-hi: #ffffff;
    --texto-mid: #e8f1fd;
    --texto-dim: #c3d3eb;
    --azul-claro: #91beff;
    --azul-neon: #91beff;
  }

  &[id='contato']::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url('/svgs/elemento%20apoio.svg') no-repeat center / cover;
    opacity: 0.035;
    pointer-events: none;
  }

  position: relative;
  overflow: hidden;

  @media (max-width: 767px) {
    padding: 68px 20px;
  }
`;

const SectionInner = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const SectionLabel = styled.p`
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  color: var(--azul-claro);
  text-transform: uppercase;
  letter-spacing: 0.2em;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: '';
    width: 28px;
    height: 1px;
    background: linear-gradient(90deg, var(--azul-claro), transparent);
    box-shadow: 0 0 8px rgba(67, 152, 241, 0.6);
  }

  &.centered {
    justify-content: center;

    &::after {
      content: '';
      width: 28px;
      height: 1px;
      background: linear-gradient(-90deg, var(--azul-claro), transparent);
      box-shadow: 0 0 8px rgba(67, 152, 241, 0.6);
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(28px, 3.5vw, 42px);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--texto-hi);
  margin-bottom: 16px;
`;

const SectionSubtitle = styled.p`
  font-size: 17px;
  line-height: 1.7;
  color: var(--texto-dim);
  max-width: 580px;
  margin-bottom: 56px;
`;

/* Símbolo original da marca como textura decorativa. */
const BrandWatermark = styled.img`
  position: absolute;
  right: -140px;
  top: 50%;
  transform: translateY(-50%);
  width: 560px;
  height: 560px;
  opacity: 0.05;
  pointer-events: none;
  z-index: 0;
`;

/* ─── Seção 2 – Proposta de Valor ────────────────────────────────────── */
const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);

    & > *:last-child:nth-child(odd) {
      grid-column: 1 / -1;
      max-width: calc(50% - 14px);
      justify-self: center;
    }
  }
  @media (max-width: 599px) {
    grid-template-columns: 1fr;

    & > *:last-child:nth-child(odd) {
      max-width: 100%;
    }
  }
`;

const ValueCard = styled(motion.div)`
  position: relative;
  overflow: hidden;
  background: linear-gradient(160deg, #ffffff, #e8f1fd);
  border: 1px solid rgba(67, 152, 241, 0.16);
  border-radius: 20px;
  padding: 34px 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  transition: transform 0.28s ease, border-color 0.28s ease,
    box-shadow 0.28s ease;

  /* hairline luminosa no topo */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 24px;
    right: 24px;
    height: 1px;
    background: linear-gradient(160deg, #ffffff, #e8f1fd);
  }

  /* numeral fantasma */
  .num {
    position: absolute;
    top: 18px;
    right: 24px;
    font-family: var(--font-mono);
    font-size: 56px;
    font-weight: 500;
    line-height: 1;
    color: rgba(67, 152, 241, 0.1);
    user-select: none;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(67, 152, 241, 0.5);
    box-shadow: 0 24px 60px rgba(8, 26, 55, 0.1),
      0 0 40px rgba(0, 86, 237, 0.18);
  }
`;

/* Superfícies dos ícones na nova paleta. */
const IconBox = styled.div`
  width: 54px;
  height: 60px;
  border-radius: 16px;
  background: var(--gradiente-cta);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
  filter: drop-shadow(0 6px 16px rgba(0, 86, 237, 0.45));
`;

const ValueCardTitle = styled.h3`
  font-size: 19px;
  font-weight: 700;
  color: var(--texto-hi);
`;

const ValueCardText = styled.p`
  font-size: 15px;
  line-height: 1.65;
  color: var(--texto-mid);
`;

/* ─── Divisor "sistema" ──────────────────────────────────────────────── */
const SistemaDivider = styled.div`
  position: relative;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(67, 152, 241, 0.22) 25%,
    rgba(67, 152, 241, 0.22) 75%,
    transparent
  );

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120px;
    height: 2px;
    transform: translate(-50%, -50%);
    background: var(--gradiente-cta);
    border-radius: 2px;
    box-shadow: 0 0 20px 3px rgba(67, 152, 241, 0.7);
    animation: divPulse 3.2s ease-in-out infinite;
  }

  @keyframes divPulse {
    0%,
    100% {
      opacity: 0.55;
      width: 90px;
    }
    50% {
      opacity: 1;
      width: 220px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
    }
  }
`;

/* ─── Seção 4 – Tour em vídeo ────────────────────────────────────────
   Espelha a trilha do módulo de Treinamentos (menu de módulos colapsáveis
   à esquerda + player à direita), vestida com os tokens da landing.

   Eixo vertical compartilhado pelas bolinhas e pelas linhas conectoras:
   mexer no RAIL_CENTER reposiciona bolinha e linha juntas. O DOT_CENTER_Y
   é o centro da PRIMEIRA linha do título — assim um módulo com título de
   duas linhas não tira a bolinha do prumo. */
const RAIL_CENTER = 11;
const RAIL_WIDTH = 2;
const DOT_SIZE = 10;
const MODULE_TITLE_LINE_HEIGHT = 20;
const MODULE_HEADER_PADDING_Y = 12;
const DOT_CENTER_Y = MODULE_HEADER_PADDING_Y + MODULE_TITLE_LINE_HEIGHT / 2;
const CONTENT_INSET = RAIL_CENTER + 18;

const VideoTourLayout = styled.div`
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 28px;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 260px minmax(0, 1fr);
    gap: 20px;
  }

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const VideoModuleNav = styled.nav`
  display: flex;
  flex-direction: column;
  max-height: 520px;
  overflow-y: auto;
  /* Sem isso a barra entra na cor padrão do SO e destoa do tema escuro. */
  scrollbar-width: thin;
  scrollbar-color: rgba(67, 152, 241, 0.45) transparent;
  padding: 12px 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: rgba(67, 152, 241, 0.45);
  }
  border-radius: 14px;
  background: var(--bg-2);
  border: 1px solid rgba(67, 152, 241, 0.22);
  box-shadow: 0 24px 60px rgba(8, 26, 55, 0.08);

  @media (max-width: 767px) {
    max-height: 320px;
  }
`;

const VideoModuleList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
`;

const VideoModuleItem = styled.li<{
  $isFirst: boolean;
  $isLast: boolean;
  $isOpen: boolean;
}>`
  position: relative;

  /* Linha conectora: cobre o item inteiro, então encosta na linha do item
     seguinte e liga as bolinhas sem gap. Com o módulo aberto, é ela que
     delimita à esquerda a lista de aulas. */
  &::before {
    content: '';
    position: absolute;
    top: ${({ $isFirst }) => ($isFirst ? `${DOT_CENTER_Y}px` : '0')};
    bottom: ${({ $isLast, $isOpen }) =>
      $isLast && !$isOpen ? `calc(100% - ${DOT_CENTER_Y}px)` : '0'};
    left: ${RAIL_CENTER}px;
    width: ${RAIL_WIDTH}px;
    transform: translateX(-50%);
    background: rgba(67, 152, 241, 0.22);
  }
`;

const VideoModuleTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-family: var(--font-principal);
  font-size: 14px;
  font-weight: 600;
  line-height: ${MODULE_TITLE_LINE_HEIGHT}px;
  overflow-wrap: anywhere;
  text-align: left;
  transition: color 0.2s;
`;

const VideoModuleChevron = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: ${MODULE_TITLE_LINE_HEIGHT}px;
  color: var(--texto-dim);
  transition: transform 0.25s, color 0.2s;
  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
`;

/* Bolinha do módulo: fica por cima da linha, que passa por trás — assim a
   linha encosta nela sem gap. O halo é o "charme" do ponto atual. */
const VideoModuleDot = styled.span<{ $isActive: boolean }>`
  position: absolute;
  z-index: 1;
  left: ${RAIL_CENTER}px;
  top: ${DOT_CENTER_Y}px;
  transform: translate(-50%, -50%);
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border-radius: 50%;
  transition: background 0.2s, box-shadow 0.2s;
  background: ${({ $isActive }) =>
    $isActive ? 'var(--azul-claro)' : 'rgba(74, 96, 128, 0.45)'};
  box-shadow: ${({ $isActive }) =>
    $isActive
      ? '0 0 10px 1px rgba(67, 152, 241, 0.75)'
      : '0 0 5px 0 rgba(74, 96, 128, 0.3)'};
`;

const VideoModuleHeader = styled.button<{ $isActive: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: ${MODULE_HEADER_PADDING_Y}px 8px ${MODULE_HEADER_PADDING_Y}px
    ${CONTENT_INSET}px;
  border: none;
  border-radius: 8px;
  background: none;
  cursor: pointer;
  transition: background 0.2s;

  ${VideoModuleTitle} {
    color: ${({ $isActive }) =>
      $isActive ? 'var(--azul-neon)' : 'var(--texto-mid)'};
  }

  &:hover {
    background: rgba(0, 86, 237, 0.1);

    ${VideoModuleTitle}, ${VideoModuleChevron} {
      color: var(--azul-neon);
    }

    ${VideoModuleDot} {
      background: var(--azul-claro);
      box-shadow: 0 0 10px 1px rgba(67, 152, 241, 0.75);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--azul-claro);
    outline-offset: -2px;
  }
`;

const VideoLessonList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
`;

const VideoLessonItem = styled.li<{ $isActive: boolean }>`
  position: relative;
  padding: 2px 0;

  /* Trecho da linha referente a esta aula: transparente deixa passar a linha
     do módulo; na aula atual acende e ganha o halo. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${RAIL_CENTER}px;
    width: ${RAIL_WIDTH}px;
    transform: translateX(-50%);
    transition: background 0.2s, box-shadow 0.2s;
    background: ${({ $isActive }) =>
      $isActive ? 'var(--azul-claro)' : 'transparent'};
    box-shadow: ${({ $isActive }) =>
      $isActive ? '0 0 8px 0 rgba(67, 152, 241, 0.7)' : 'none'};
  }
`;

const VideoLessonButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: calc(100% - ${CONTENT_INSET}px);
  margin-left: ${CONTENT_INSET}px;
  padding: 8px;
  border: none;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  font-family: var(--font-principal);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 16px;
  overflow-wrap: anywhere;
  transition: background 0.2s, color 0.2s;
  color: ${({ $isActive }) =>
    $isActive ? 'var(--azul-neon)' : 'var(--texto-dim)'};
  background: ${({ $isActive }) =>
    $isActive ? 'rgba(0, 86, 237, 0.18)' : 'transparent'};

  &:hover {
    color: var(--azul-neon);
    background: ${({ $isActive }) =>
      $isActive ? 'rgba(0, 86, 237, 0.26)' : 'rgba(0, 86, 237, 0.1)'};
  }

  &:focus-visible {
    outline: 2px solid var(--azul-claro);
    outline-offset: -2px;
  }
`;

const VideoStage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

const VideoEyebrow = styled.span`
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--azul-claro);
`;

const VideoLessonTitle = styled.h3`
  font-family: var(--font-principal);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--texto-hi);

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

/* Proporção 16:9 via padding-top: o quadro não "pula" enquanto o iframe
   carrega, e a landing rola normalmente (diferente da tela interna, que
   trava na viewport). */
const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 14px;
  overflow: hidden;
  background: #081a37;
  border: 1px solid rgba(67, 152, 241, 0.22);
  box-shadow: 0 34px 90px rgba(8, 26, 55, 0.12), 0 0 60px rgba(0, 86, 237, 0.14);

  > iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

/* Enquanto a aula não tem vídeo publicado. */
const VideoPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  text-align: center;
  background: linear-gradient(
    120deg,
    rgba(67, 152, 241, 0.05) 30%,
    rgba(67, 152, 241, 0.12) 50%,
    rgba(67, 152, 241, 0.05) 70%
  );
  background-size: 200% 100%;
  animation: videoShimmer 2.6s linear infinite;

  span {
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #e8f1fd;
  }

  svg {
    color: #91beff;
  }

  @keyframes videoShimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: 0 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const VideoNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const VideoNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-principal);
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 999px;
  cursor: pointer;
  color: var(--azul-neon);
  background: #ffffff;
  border: 1px solid rgba(67, 152, 241, 0.35);
  transition: background 0.2s, border-color 0.2s, transform 0.2s, opacity 0.2s;

  &:hover:not(:disabled) {
    background: rgba(0, 86, 237, 0.32);
    border-color: rgba(0, 86, 237, 0.7);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--azul-claro);
    outline-offset: 2px;
  }
`;

/* Cursor piscante do efeito de digitação na descrição. */
const TypingCaret = styled.span<{ $visible: boolean }>`
  display: inline-block;
  width: 2px;
  height: 1.05em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: var(--azul-claro);
  box-shadow: 0 0 8px rgba(67, 152, 241, 0.9);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  animation: caretBlink 0.9s steps(1, end) infinite;

  @keyframes caretBlink {
    0%,
    50% {
      opacity: 1;
    }
    50.01%,
    100% {
      opacity: 0;
    }
  }
`;

/* ─── Seção 5 – Diferenciais ─────────────────────────────────────────── */
const DiffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 24px;
  align-items: stretch;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const DiffItem = styled(motion.div)`
  display: flex;
  gap: 18px;
  align-items: flex-start;
  height: 100%;
  padding: 24px 26px;
  background: rgba(232, 241, 253, 0.04);
  border: 1px solid rgba(67, 152, 241, 0.13);
  border-radius: 16px;
  transition: transform 0.25s ease, border-color 0.25s ease,
    box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(67, 152, 241, 0.45);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15), 0 0 24px rgba(0, 86, 237, 0.15);
  }
`;

const DiffIcon = styled.div`
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 12px;
  background: rgba(0, 86, 237, 0.16);
  border: 1px solid rgba(67, 152, 241, 0.3);
  color: var(--azul-neon);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DiffTitle = styled.h3`
  font-size: 16.5px;
  font-weight: 700;
  color: var(--texto-hi);
  margin-bottom: 6px;
`;

const DiffText = styled.p`
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--texto-dim);
`;

/* ─── Seção 6 – CTA / Formulário ────────────────────────────────────── */
const FormGrid = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const FormPanel = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(67, 152, 241, 0.2);
  border-radius: 24px;
  padding: 44px;
  box-shadow: 0 30px 80px rgba(8, 26, 55, 0.09);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 40px;
    right: 40px;
    height: 1px;
    background: #ffffff;
  }

  @media (max-width: 599px) {
    padding: 28px 20px;
  }
`;

const FormFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-bottom: 18px;
`;

const FormRow = styled.div``;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: rgba(8, 26, 55, 0.9);
  margin-bottom: 7px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border-radius: 10px;
  border: 1.5px solid rgba(67, 152, 241, 0.22);
  font-family: var(--font-principal);
  font-size: 15px;
  color: var(--texto-hi);
  background: #f5f8fe;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  outline: none;

  &:focus {
    border-color: var(--azul-claro);
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(0, 86, 237, 0.2), 0 0 20px rgba(0, 86, 237, 0.15);
  }

  &::placeholder {
    color: #61728c;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 12px 15px;
  border-radius: 10px;
  border: 1.5px solid rgba(67, 152, 241, 0.22);
  font-family: var(--font-principal);
  font-size: 15px;
  line-height: 1.5;
  color: var(--texto-hi);
  background: #f5f8fe;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  outline: none;
  resize: vertical;

  &:focus {
    border-color: var(--azul-claro);
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(0, 86, 237, 0.2), 0 0 20px rgba(0, 86, 237, 0.15);
  }

  &::placeholder {
    color: #61728c;
  }
`;

const FormSubmitBtn = styled.button`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 15px;
  border-radius: 12px;
  border: none;
  background: var(--gradiente-cta);
  color: #fff;
  font-family: var(--font-principal);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  box-shadow: 0 8px 32px rgba(0, 86, 237, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
  transition: transform 0.2s, box-shadow 0.2s;

  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(
      105deg,
      transparent 42%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 58%
    );
    transform: translateX(-130%);
    animation: btnSweepForm 5s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes btnSweepForm {
    0%,
    62% {
      transform: translateX(-130%);
    }
    82%,
    100% {
      transform: translateX(130%);
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 44px rgba(0, 86, 237, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.22);
  }
  &:focus-visible {
    outline: 2px solid var(--azul-claro);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    transform: none;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const FormNote = styled.p`
  font-size: 13px;
  color: var(--texto-dim);
  text-align: center;
  margin-top: 16px;
`;

/* ─── Footer ─────────────────────────────────────────────────────────── */
const FooterEl = styled.footer`
  --texto-dim: #c3d3eb;
  --azul-claro: #91beff;
  background: #081a37;
  border-top: 1px solid rgba(67, 152, 241, 0.1);
  padding: 52px 48px 36px;

  @media (max-width: 767px) {
    padding: 40px 20px 24px;
  }
`;

const FooterInner = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
`;

const FooterContacts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  a {
    color: var(--texto-dim);
    font-size: 14px;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: color 0.2s;
    &:hover {
      color: var(--azul-neon);
    }
  }
`;

const FooterCopy = styled.p`
  font-size: 13px;
  color: rgba(136, 150, 166, 0.45);
`;

/* ─── SVG Icons ──────────────────────────────────────────────────────── */
const IconField: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M9 21V9" />
  </svg>
);

const IconAdmin: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconChart: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const IconUsers: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconPayroll: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const IconClock: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconHardhat: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 18h20v2a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-2z" />
    <path d="M12 2a9 9 0 0 1 9 9v5H3v-5a9 9 0 0 1 9-9z" />
    <line x1="12" y1="2" x2="12" y2="11" />
    <line x1="3" y1="13" x2="12" y2="11" />
    <line x1="21" y1="13" x2="12" y2="11" />
  </svg>
);

const IconCart: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const IconDoc: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const IconClipboard: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);

const IconReport: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <line x1="8" y1="9" x2="11" y2="9" />
  </svg>
);

const IconDashboard: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const IconLock: FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const IconArrow: FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const IconChevronLeft: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconChevronRight: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IconBolt: FC<{ size?: number }> = ({ size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);

const IconChevronDown: FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const IconVideoOff: FC = () => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 16v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2" />
    <path d="M10 6h4a2 2 0 0 1 2 2v2l6-4v10l-3-2" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

/* ─── Animation helpers ──────────────────────────────────────────────── */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fadeUpVariant: any = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: 'easeOut' },
  }),
};

function InViewWrapper({
  children,
  delay = 0,
  style,
}: {
  children: ReactNode;
  delay?: number;
  style?: CSSProperties;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      custom={delay}
      variants={fadeUpVariant}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ───────────────────────────────────────────────────────────── */
const modules = [
  {
    icon: <IconUsers />,
    title: 'Gestão de Funcionários',
    desc: 'Cadastro completo, documentação, histórico e controle de equipes por obra.',
  },
  {
    icon: <IconPayroll />,
    title: 'Folha de Pagamento',
    desc: 'Cálculo automatizado de salários, encargos, benefícios e holerites.',
  },
  {
    icon: <IconClock />,
    title: 'Cartão de Ponto',
    desc: 'Jornada, banco de horas, faltas, atrasos e horas extras integrados à folha.',
  },
  {
    icon: <IconHardhat />,
    title: 'Gestão de Obras',
    desc: 'Cadastro de obras, alocação de equipes, cronograma e status em tempo real.',
  },
  {
    icon: <IconCart />,
    title: 'Pedidos de Compras',
    desc: 'Solicitações, cotações, aprovações e rastreamento de materiais por obra.',
  },
  {
    icon: <IconDoc />,
    title: 'Ciclo de Vida de Propostas',
    desc: 'Criação, envio, negociação, aprovação e conversão de propostas em contratos.',
  },
  {
    icon: <IconClipboard />,
    title: 'RDO',
    desc: 'Registro padronizado de atividades, clima, mão de obra e ocorrências diárias.',
  },
  {
    icon: <IconReport />,
    title: 'Relatórios Gerais',
    desc: 'Relatórios customizáveis por módulo com exportação em PDF e Excel.',
  },
  {
    icon: <IconDashboard />,
    title: 'Dashboard Estatístico',
    desc: 'KPIs segmentados por categoria: financeiro, operacional, RH e comercial.',
  },
];

const diferenciais = [
  {
    icon: <IconUsers />,
    title: 'Gestão de Funcionários',
    text: 'Cadastro completo, documentação, histórico e controle de equipes por obra.',
  },
  {
    icon: <IconPayroll />,
    title: 'Folha de Pagamento',
    text: 'Cálculo automatizado de salários, encargos, benefícios e holerites.',
  },
  {
    icon: <IconClock />,
    title: 'Cartão de Ponto',
    text: 'Jornada, banco de horas, faltas, atrasos e horas extras integrados à folha.',
  },
  {
    icon: <IconHardhat />,
    title: 'Gestão de Obras',
    text: 'Cadastro de obras, alocação de equipes, cronograma e status em tempo real.',
  },
  {
    icon: <IconCart />,
    title: 'Pedidos de Compras',
    text: 'Solicitações, cotações, aprovações e rastreamento de materiais por obra.',
  },
  {
    icon: <IconDoc />,
    title: 'Ciclo de Vida de Propostas',
    text: 'Criação, envio, negociação, aprovação e conversão de propostas em contratos.',
  },
  {
    icon: <IconClipboard />,
    title: 'RDO',
    text: 'Registro padronizado de atividades, clima, mão de obra e ocorrências diárias.',
  },
  {
    icon: <IconReport />,
    title: 'Relatórios Gerais',
    text: 'Relatórios customizáveis por módulo com exportação em PDF e Excel.',
  },
  {
    icon: <IconDashboard />,
    title: 'Dashboard Estatístico',
    text: 'KPIs segmentados por categoria: financeiro, operacional, RH e comercial.',
  },
  {
    icon: <IconLock />,
    title: 'Controle de Acesso',
    text: 'Perfis e permissões por usuário, garantindo que cada colaborador acesse apenas o que é necessário para sua função.',
  },
];

/* ─── Hero — "O Núcleo": reator hexagonal VMCORE ────────────────────── */
/*
 * O hexágono do logo vira um núcleo de energia: anéis orbitais giram ao
 * redor, traços de circuito ligam o core aos módulos do sistema e pulsos
 * de luz percorrem os traços — metade entrando (o campo alimenta o core),
 * metade saindo (o core alimenta a operação).
 */
const SceneWrap = styled.div`
  width: 100%;
  max-width: 560px;
  position: relative;
  aspect-ratio: 1 / 1;

  /* Glow azul de fundo */
  &::before {
    content: '';
    position: absolute;
    inset: 6%;
    background: radial-gradient(
      circle at 50% 52%,
      rgba(0, 86, 237, 0.26) 0%,
      rgba(0, 86, 237, 0.1) 40%,
      transparent 68%
    );
    filter: blur(14px);
    pointer-events: none;
  }

  svg {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  /* A cena inteira respira */
  .scene-float {
    animation: sceneFloat 7s ease-in-out infinite;
    transform-origin: 50% 50%;
  }
  @keyframes sceneFloat {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-9px);
    }
  }

  /* Glow do núcleo pulsando */
  .core-glow {
    transform-box: fill-box;
    transform-origin: center;
    animation: coreBreath 4s ease-in-out infinite;
  }
  @keyframes coreBreath {
    0%,
    100% {
      opacity: 0.7;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.08);
    }
  }

  /* Ondas hexagonais expandindo do núcleo */
  .ring-pulse {
    transform-box: fill-box;
    transform-origin: center;
    animation: ringPulse 3.6s ease-out infinite;
    opacity: 0;
  }
  .ring-pulse.r2 {
    animation-delay: 1.2s;
  }
  .ring-pulse.r3 {
    animation-delay: 2.4s;
  }
  @keyframes ringPulse {
    0% {
      opacity: 0.65;
      transform: scale(1);
    }
    75% {
      opacity: 0;
      transform: scale(1.6);
    }
    100% {
      opacity: 0;
      transform: scale(1.6);
    }
  }

  /* Anéis orbitais */
  .orbit {
    transform-box: fill-box;
    transform-origin: center;
    animation: orbitSpin 52s linear infinite;
  }
  .orbit.reverse {
    animation: orbitSpin 84s linear infinite reverse;
  }
  .comet {
    transform-box: fill-box;
    transform-origin: center;
    animation: orbitSpin 7s linear infinite;
  }
  @keyframes orbitSpin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Pulsos de energia percorrendo os traços de circuito */
  .pulse-out {
    stroke-dasharray: 16 84;
    animation: pulseOut 2.6s linear infinite;
  }
  .pulse-in {
    stroke-dasharray: 16 84;
    animation: pulseIn 2.6s linear infinite;
  }
  .pulse-out.d2,
  .pulse-in.d2 {
    animation-delay: 0.85s;
  }
  .pulse-out.d3,
  .pulse-in.d3 {
    animation-delay: 1.7s;
  }
  @keyframes pulseOut {
    from {
      stroke-dashoffset: 100;
    }
    to {
      stroke-dashoffset: 0;
    }
  }
  @keyframes pulseIn {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: 100;
    }
  }

  /* Chips dos módulos flutuando */
  .node {
    transform-box: fill-box;
    transform-origin: center;
    animation: nodeFloat 5.5s ease-in-out infinite;
  }
  .node.n2 {
    animation-delay: 0.9s;
  }
  .node.n3 {
    animation-delay: 1.8s;
  }
  .node.n4 {
    animation-delay: 2.7s;
  }
  .node.n5 {
    animation-delay: 3.6s;
  }
  .node.n6 {
    animation-delay: 4.5s;
  }
  @keyframes nodeFloat {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-5px);
    }
  }

  /* LEDs de status */
  .led {
    animation: ledBlink 1.8s steps(1, end) infinite;
  }
  .led.l2 {
    animation-delay: 0.45s;
  }
  .led.l3 {
    animation-delay: 0.9s;
  }
  .led.l4 {
    animation-delay: 1.35s;
  }
  @keyframes ledBlink {
    0%,
    48% {
      opacity: 1;
    }
    52%,
    100% {
      opacity: 0.25;
    }
  }

  /* Arcos elétricos ocasionais */
  .flash {
    opacity: 0;
    animation: arcFlash 5.4s linear infinite;
  }
  .flash.f2 {
    animation-duration: 7.1s;
    animation-delay: 2.3s;
  }
  @keyframes arcFlash {
    0%,
    86% {
      opacity: 0;
    }
    88%,
    92% {
      opacity: 1;
    }
    94%,
    100% {
      opacity: 0;
    }
  }

  /* Partículas subindo */
  .spark {
    animation: sparkFloat 5s ease-in-out infinite;
  }
  .spark.s2 {
    animation-delay: 1.3s;
  }
  .spark.s3 {
    animation-delay: 2.6s;
  }
  .spark.s4 {
    animation-delay: 3.9s;
  }
  @keyframes sparkFloat {
    0% {
      opacity: 0;
      transform: translateY(8px);
    }
    30% {
      opacity: 1;
    }
    70% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: translateY(-18px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .scene-float,
    .core-glow,
    .orbit,
    .comet,
    .node,
    .led {
      animation: none;
    }
    .ring-pulse,
    .pulse-out,
    .pulse-in,
    .flash,
    .spark,
    .comet {
      display: none;
    }
  }
`;

/* Módulos conectados ao núcleo e seus traços de circuito. */
const coreNodes = [
  { x: 498, y: 285, label: 'RDO', dir: 'out' },
  { x: 389, y: 474, label: 'COMPRAS', dir: 'in' },
  { x: 171, y: 474, label: 'FOLHA', dir: 'out' },
  { x: 62, y: 285, label: 'PONTO', dir: 'in' },
  { x: 171, y: 96, label: 'NF-E', dir: 'out' },
  { x: 389, y: 96, label: 'OBRAS', dir: 'in' },
] as const;

const coreTraces = [
  'M345.7,285 L455,285',
  'M312.9,342 L389,418 L389,459',
  'M247.1,342 L171,418 L171,459',
  'M214.3,285 L105,285',
  'M247.1,228 L171,152 L171,111',
  'M312.9,228 L389,152 L389,111',
];

function HeroCoreScene(): JSX.Element {
  return (
    <SceneWrap>
      <svg
        viewBox="0 0 560 560"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="coreGlowGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(0,86,237,0.55)" />
            <stop offset="45%" stopColor="rgba(0,86,237,0.18)" />
            <stop offset="100%" stopColor="rgba(0,86,237,0)" />
          </radialGradient>
          <radialGradient id="floorGlowGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(0,86,237,0.35)" />
            <stop offset="100%" stopColor="rgba(0,86,237,0)" />
          </radialGradient>
          <linearGradient id="hexFillGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0056ED" />
            <stop offset="1" stopColor="#081A37" />
          </linearGradient>
          <filter id="neonGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="2.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g className="scene-float">
          {/* Hexágonos-fantasma ao fundo */}
          <polygon
            points="40,0 74.6,20 74.6,60 40,80 5.4,60 5.4,20"
            transform="translate(112,117) scale(4.2)"
            fill="none"
            stroke="rgba(0,86,237,0.07)"
            strokeWidth="0.5"
          />
          <polygon
            points="40,0 74.6,20 74.6,60 40,80 5.4,60 5.4,20"
            transform="translate(64,69) scale(5.4)"
            fill="none"
            stroke="rgba(0,86,237,0.045)"
            strokeWidth="0.4"
          />

          {/* Glow de chão */}
          <ellipse
            cx="280"
            cy="528"
            rx="185"
            ry="24"
            fill="url(#floorGlowGrad)"
          />

          {/* Anéis orbitais */}
          <circle
            className="orbit"
            cx="280"
            cy="285"
            r="168"
            fill="none"
            stroke="rgba(0,86,237,0.26)"
            strokeWidth="1"
            strokeDasharray="3 10"
          />
          <circle
            className="orbit reverse"
            cx="280"
            cy="285"
            r="202"
            fill="none"
            stroke="rgba(0,86,237,0.14)"
            strokeWidth="1"
            strokeDasharray="1 15"
          />
          {/* Cometa varrendo a órbita */}
          <circle
            className="comet"
            cx="280"
            cy="285"
            r="202"
            fill="none"
            stroke="#4398F1"
            strokeWidth="2"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray="9 91"
            filter="url(#neonGlow)"
            opacity="0.9"
          />

          {/* Traços de circuito (base) + pads */}
          <g
            fill="none"
            stroke="rgba(0,86,237,0.22)"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            {coreTraces.map((d) => (
              <path key={d} d={d} />
            ))}
          </g>
          <g fill="rgba(0,86,237,0.85)">
            <circle cx="345.7" cy="285" r="2.6" />
            <circle cx="312.9" cy="342" r="2.6" />
            <circle cx="247.1" cy="342" r="2.6" />
            <circle cx="214.3" cy="285" r="2.6" />
            <circle cx="247.1" cy="228" r="2.6" />
            <circle cx="312.9" cy="228" r="2.6" />
          </g>

          {/* Pulsos de energia sobre os traços */}
          {coreTraces.map((d, i) => (
            <path
              key={`pulse-${coreNodes[i].label}`}
              className={`${
                coreNodes[i].dir === 'out' ? 'pulse-out' : 'pulse-in'
              } d${(i % 3) + 1}`}
              d={d}
              pathLength={100}
              fill="none"
              stroke="#0056ED"
              strokeWidth="2.5"
              strokeLinecap="round"
              filter="url(#neonGlow)"
              opacity="0.95"
            />
          ))}

          {/* Ondas hexagonais expandindo do núcleo */}
          {[1, 2, 3].map((n) => (
            <polygon
              key={n}
              className={`ring-pulse r${n}`}
              points="40,0 74.6,20 74.6,60 40,80 5.4,60 5.4,20"
              transform="translate(204,209) scale(1.9)"
              fill="none"
              stroke="rgba(67,152,241,0.7)"
              strokeWidth="1"
            />
          ))}

          {/* Glow atrás do núcleo */}
          <circle
            className="core-glow"
            cx="280"
            cy="285"
            r="118"
            fill="url(#coreGlowGrad)"
          />

          {/* Núcleo: o hexágono do logo, energizado */}
          <g transform="translate(204,209) scale(1.9)">
            <polygon
              points="40,0 74.6,20 74.6,60 40,80 5.4,60 5.4,20"
              fill="url(#hexFillGrad)"
            />
            <polygon
              points="40,0 74.6,20 74.6,60 40,80 5.4,60 5.4,20"
              fill="none"
              stroke="#4398F1"
              strokeWidth="1"
              strokeOpacity="0.85"
              filter="url(#neonGlow)"
            />
            <path
              d="M26,22 L40,58 L54,22 L48.5,22 L40,48 L31.5,22 Z"
              fill="#FFFFFF"
              filter="url(#neonGlow)"
            />
          </g>

          {/* Arcos elétricos ocasionais */}
          <polyline
            className="flash"
            points="352,236 366,228 358,243 372,235"
            fill="none"
            stroke="#FFD666"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#neonGlow)"
          />
          <polyline
            className="flash f2"
            points="206,338 192,346 200,331 186,339"
            fill="none"
            stroke="#FFD666"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#neonGlow)"
          />

          {/* Chips dos módulos */}
          {coreNodes.map((node, i) => (
            <g className={`node n${i + 1}`} key={node.label}>
              <rect
                x={node.x - 43}
                y={node.y - 15}
                width="86"
                height="30"
                rx="8"
                fill="rgba(8,26,55,0.92)"
                stroke="rgba(0,86,237,0.38)"
                strokeWidth="1"
              />
              <circle
                className={`led l${(i % 4) + 1}`}
                cx={node.x - 31}
                cy={node.y}
                r="3"
                fill={
                  i % 3 === 0 ? '#10B981' : i % 3 === 1 ? '#4398F1' : '#F59E0B'
                }
              />
              <text
                x={node.x + 5}
                y={node.y + 3.5}
                textAnchor="middle"
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  letterSpacing: '1.5px',
                  fill: '#E8F1FD',
                }}
              >
                {node.label}
              </text>
            </g>
          ))}

          {/* Partículas */}
          <g fill="#4398F1">
            <circle className="spark s1" cx="150" cy="200" r="2.5" />
            <circle className="spark s2" cx="416" cy="352" r="2" />
            <circle className="spark s3" cx="330" cy="140" r="2" />
            <circle className="spark s4" cx="120" cy="380" r="2.5" />
          </g>
        </g>
      </svg>
    </SceneWrap>
  );
}

/* ─── Page Component ─────────────────────────────────────────────────── */
const VmcoreErpLandingPage: NextPage & { layoutMode?: 'none' } = () => {
  const smoothScroll = (id: string) => (e: MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    // O scroll real acontece no container #__next (height:100%; overflow:auto),
    // não no window — então scrollIntoView é o que funciona aqui.
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [scrolled, setScrolled] = useState(false);

  // Tour em vídeo: trilha achatada (para o "anterior/próxima"), aula atual e
  // acordeão de módulos — um aberto por vez, como na tela interna.
  const tourLessons = useMemo(
    () => flattenTrainingLessons(trainingModules),
    []
  );
  const [activeLessonId, setActiveLessonId] = useState(
    tourLessons.length ? tourLessons[0].lesson.id : ''
  );
  const [openModuleId, setOpenModuleId] = useState(
    trainingModules.length ? trainingModules[0].id : null
  );
  // Efeito de digitação do título da aula atual.
  const [typedCaption, setTypedCaption] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  const activeLesson = useMemo(() => {
    if (!tourLessons.length) return null;
    return (
      tourLessons.find((item) => item.lesson.id === activeLessonId) ||
      tourLessons[0]
    );
  }, [tourLessons, activeLessonId]);

  // Parallax da composição visual, respeitando a preferência de movimento.
  const reduceMotion = useReducedMotion();
  const heroMouseX = useMotionValue(0);
  const heroMouseY = useMotionValue(0);
  const sceneRotateY = useSpring(
    useTransform(heroMouseX, [-0.5, 0.5], [-7, 7]),
    { stiffness: 60, damping: 18 }
  );
  const sceneRotateX = useSpring(
    useTransform(heroMouseY, [-0.5, 0.5], [7, -7]),
    { stiffness: 60, damping: 18 }
  );

  const handleHeroMouseMove = (e: MouseEvent<HTMLElement>): void => {
    if (reduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    heroMouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    heroMouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleHeroMouseLeave = (): void => {
    heroMouseX.set(0);
    heroMouseY.set(0);
  };

  // Escolher uma aula sempre abre o módulo dela (importa ao usar o
  // "anterior/próxima" atravessando a fronteira entre dois módulos).
  const goToLesson = (lessonId: string): void => {
    const target = tourLessons.find((item) => item.lesson.id === lessonId);
    if (!target) return;
    setActiveLessonId(target.lesson.id);
    setOpenModuleId(target.module.id);
  };

  // Acordeão: abrir um módulo fecha o que estava aberto.
  const toggleModule = (moduleId: string): void => {
    setOpenModuleId((current) => (current === moduleId ? null : moduleId));
  };

  // Digita o título da aula atual (~1,8s). Sem auto-avanço: quem está
  // assistindo um vídeo não quer a seção trocando sozinha.
  useEffect(() => {
    const caption = activeLesson
      ? activeLesson.lesson.descricao || activeLesson.lesson.titulo
      : '';
    const TYPING_MS = 1800;
    const stepMs = Math.max(
      16,
      Math.floor(TYPING_MS / Math.max(caption.length, 1))
    );

    setTypedCaption('');
    setIsTyping(true);

    let charIndex = 0;
    const typingTimer = setInterval(() => {
      charIndex += 1;
      setTypedCaption(caption.slice(0, charIndex));
      if (charIndex >= caption.length) {
        clearInterval(typingTimer);
        setIsTyping(false);
      }
    }, stepMs);

    return () => clearInterval(typingTimer);
  }, [activeLesson]);

  const [formState, setFormState] = useState({
    nome: '',
    mensagem: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!formState.nome.trim()) errs.nome = 'Nome obrigatório';
    if (!formState.mensagem.trim()) errs.mensagem = 'Mensagem obrigatória';

    return errs;
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }
    setFormErrors({});

    const message = `Olá, meu nome é ${formState.nome.trim()}.\n\n${formState.mensagem.trim()}`;
    const whatsappUrl = `https://wa.me/${WHATSAPP_DESTINATION}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleChange =
    (field: string) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormState((s) => ({ ...s, [field]: e.target.value }));
      if (formErrors[field])
        setFormErrors((s) => {
          const n = { ...s };
          delete n[field];
          return n;
        });
    };
  const previousLesson = activeLesson
    ? tourLessons[activeLesson.flatIndex - 1]
    : undefined;
  const nextLesson = activeLesson
    ? tourLessons[activeLesson.flatIndex + 1]
    : undefined;
  const activeEmbedUrl = activeLesson
    ? getVideoEmbedUrl(activeLesson.lesson.videoUrl)
    : '';

  return (
    <>
      <LandingGlobal />
      <Head>
        <title>
          VMCORE ERP — Gestão completa de obras e instalações de engenharia
        </title>
        <meta
          name="description"
          content="ERP ponta a ponta para empresas de engenharia. Gestão de obras, RDO digital, folha de pagamento, cartão de ponto, pedidos de compras e muito mais em uma única plataforma integrada."
        />
        <meta
          name="keywords"
          content="ERP para obras, gestão de obras, sistema para engenharia, ERP de engenharia, relatório diário de obra, gestão de instalações, RDO digital, controle de obras, ERP construção civil"
        />
        <meta
          property="og:title"
          content="VMCORE ERP — Gestão completa de obras e instalações"
        />
        <meta
          property="og:description"
          content="ERP ponta a ponta para empresas de engenharia. Do cartão de ponto ao relatório diário de obra."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="theme-color" content={VMCORE_BRAND.pale} />
        <link rel="icon" type="image/svg+xml" href={VMCORE_BRAND.icon} />
      </Head>

      {/* ── Header ── */}
      <Header $scrolled={scrolled} role="banner">
        <button
          type="button"
          aria-label="VMCORE – início"
          onClick={smoothScroll('hero')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
          }}
        >
          <VmcoreLogo height={32} tone="dark" animateIcon />
        </button>
        <NavLinks>
          <button type="button" onClick={smoothScroll('proposta')}>
            Propósito
          </button>
          <button type="button" onClick={smoothScroll('sistema')}>
            Sistema
          </button>
          <button type="button" onClick={smoothScroll('diferenciais')}>
            Diferenciais
          </button>
          <HeaderCta onClick={smoothScroll('contato')}>
            Entrar em contato
          </HeaderCta>
        </NavLinks>
      </Header>

      {/* ── Hero ── */}
      <HeroSection
        id="hero"
        onMouseMove={handleHeroMouseMove}
        onMouseLeave={handleHeroMouseLeave}
      >
        <Aurora $variant={1} aria-hidden="true" />
        <Aurora $variant={2} aria-hidden="true" />
        <GridHorizon aria-hidden="true" />

        <HeroInner>
          <HeroBadge
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <span className="dot" aria-hidden="true" />
            ERP para engenharia elétrica &amp; industrial
          </HeroBadge>

          <HeroTitle
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.55,
              delay: 0.1,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            Gestão completa de obras e instalações{' '}
            <em>em uma única plataforma.</em>
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.22 }}
          >
            Do cartão de ponto ao relatório diário de obra. ERP ponta a ponta
            para empresas de engenharia que precisam integrar gestão
            administrativa com operação de campo.
          </HeroSubtitle>

          <HeroCtas
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.34 }}
          >
            <BtnPrimary onClick={smoothScroll('contato')}>
              Solicitar Demonstração
            </BtnPrimary>
            <BtnSecondary onClick={smoothScroll('proposta')}>
              Conhecer Módulos <IconArrow />
            </BtnSecondary>
          </HeroCtas>

          <HeroStats
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.48 }}
          >
            <div className="stat">
              <strong>
                09<em>+</em>
              </strong>
              <span>módulos integrados</span>
            </div>
            <div className="stat">
              <strong>
                100<em>%</em>
              </strong>
              <span>web — sem instalação</span>
            </div>
            <div className="stat">
              <strong>
                NF-e <em>auto</em>
              </strong>
              <span>captura direto da SEFAZ</span>
            </div>
          </HeroStats>
        </HeroInner>

        <HeroVisual
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              rotateX: reduceMotion ? 0 : sceneRotateX,
              rotateY: reduceMotion ? 0 : sceneRotateY,
              transformPerspective: 900,
            }}
          >
            <HeroCoreScene />
          </motion.div>
        </HeroVisual>

        <ScrollCue aria-hidden="true">
          <div className="mouse">
            <span />
          </div>
          <p>explore</p>
        </ScrollCue>
      </HeroSection>

      {/* ── Ticker de módulos ── */}
      <TickerStrip aria-hidden="true">
        <TickerTrack>
          {(['a', 'b'] as const).map((copy) =>
            modules.map((m) => (
              <TickerItem key={`${copy}-${m.title}`}>
                {m.title}
                <IconBolt />
              </TickerItem>
            ))
          )}
        </TickerTrack>
      </TickerStrip>

      {/* ── Seção 2 – Proposta de Valor ── */}
      <Section id="proposta" $bg="var(--bg-1)">
        <SectionInner>
          <InViewWrapper>
            <SectionLabel>Por que o VMCORE</SectionLabel>
            <SectionTitle>
              Um ERP que entende a rotina da engenharia.
            </SectionTitle>
            <SectionSubtitle>
              Projetado para empresas que precisam integrar gestão
              administrativa com operação de campo — tudo em um só lugar, sem
              adaptações.
            </SectionSubtitle>
          </InViewWrapper>

          <ValueGrid>
            {[
              {
                icon: <IconField />,
                title: 'Operação de Campo',
                text: 'Controle obras, RDOs e propostas em tempo real, de qualquer dispositivo.',
              },
              {
                icon: <IconAdmin />,
                title: 'Gestão Administrativa',
                text: 'Funcionários, folha de pagamento, ponto e documentação centralizados.',
              },
              {
                icon: <IconChart />,
                title: 'Inteligência e Dados',
                text: 'Dashboard com KPIs segmentados por categoria: financeiro, operacional, RH e comercial.',
              },
            ].map((item, i) => (
              <InViewWrapper
                key={item.title}
                delay={i}
                style={{ height: '100%' }}
              >
                <ValueCard>
                  <span className="num" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <IconBox>{item.icon}</IconBox>
                  <ValueCardTitle>{item.title}</ValueCardTitle>
                  <ValueCardText>{item.text}</ValueCardText>
                </ValueCard>
              </InViewWrapper>
            ))}
          </ValueGrid>
        </SectionInner>
      </Section>

      <SistemaDivider id="sistema" aria-hidden="true">
        <span />
      </SistemaDivider>

      {/* ── Seção 4 – Tour em vídeo ── */}
      <Section $bg="var(--bg-0)" style={{ paddingTop: 72, paddingBottom: 72 }}>
        <SectionInner>
          <InViewWrapper>
            <SectionLabel>Tour em vídeo</SectionLabel>
            <SectionTitle>Conheça o sistema por dentro.</SectionTitle>
            <SectionSubtitle
              style={{ marginBottom: 28, maxWidth: 760, minHeight: 28 }}
              aria-live="polite"
            >
              {typedCaption}
              <TypingCaret $visible={isTyping} aria-hidden="true" />
            </SectionSubtitle>
          </InViewWrapper>

          {activeLesson && (
            <InViewWrapper delay={1}>
              <VideoTourLayout>
                <VideoModuleNav aria-label="Módulos do treinamento">
                  <VideoModuleList>
                    {trainingModules.map((modulo, moduleIndex) => {
                      const isOpen = modulo.id === openModuleId;
                      const isActiveModule =
                        modulo.id === activeLesson.module.id;

                      return (
                        <VideoModuleItem
                          key={modulo.id}
                          $isFirst={moduleIndex === 0}
                          $isLast={moduleIndex === trainingModules.length - 1}
                          $isOpen={isOpen}
                        >
                          <VideoModuleHeader
                            type="button"
                            $isActive={isActiveModule}
                            aria-expanded={isOpen}
                            onClick={() => toggleModule(modulo.id)}
                          >
                            <VideoModuleDot
                              $isActive={isActiveModule}
                              aria-hidden="true"
                            />
                            <VideoModuleTitle>{modulo.titulo}</VideoModuleTitle>
                            <VideoModuleChevron
                              $isOpen={isOpen}
                              aria-hidden="true"
                            >
                              <IconChevronDown />
                            </VideoModuleChevron>
                          </VideoModuleHeader>

                          <Collapse
                            isOpen={isOpen}
                            hasBoxShadow={false}
                            hasMarginBottom={false}
                          >
                            <VideoLessonList>
                              {(modulo.aulas || []).map((aula, aulaIndex) => {
                                const isActiveLesson =
                                  aula.id === activeLesson.lesson.id;

                                return (
                                  <VideoLessonItem
                                    key={aula.id}
                                    $isActive={isActiveLesson}
                                  >
                                    <VideoLessonButton
                                      type="button"
                                      $isActive={isActiveLesson}
                                      aria-current={
                                        isActiveLesson ? 'true' : undefined
                                      }
                                      onClick={() => goToLesson(aula.id)}
                                    >
                                      {`${aulaIndex + 1}. ${aula.titulo}`}
                                    </VideoLessonButton>
                                  </VideoLessonItem>
                                );
                              })}
                            </VideoLessonList>
                          </Collapse>
                        </VideoModuleItem>
                      );
                    })}
                  </VideoModuleList>
                </VideoModuleNav>

                <VideoStage>
                  <div>
                    <VideoEyebrow>{activeLesson.module.titulo}</VideoEyebrow>
                    <VideoLessonTitle>
                      {`Aula ${activeLesson.lessonNumber}: ${activeLesson.lesson.titulo}`}
                    </VideoLessonTitle>
                  </div>

                  <VideoFrame>
                    {activeEmbedUrl ? (
                      <iframe
                        key={activeLesson.lesson.id}
                        src={activeEmbedUrl}
                        title={activeLesson.lesson.titulo}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        allowFullScreen
                      />
                    ) : (
                      <VideoPlaceholder aria-hidden="true">
                        <IconVideoOff />
                        <span>Vídeo em produção</span>
                      </VideoPlaceholder>
                    )}
                  </VideoFrame>

                  <VideoNav>
                    <VideoNavButton
                      type="button"
                      disabled={!previousLesson}
                      onClick={() =>
                        previousLesson && goToLesson(previousLesson.lesson.id)
                      }
                    >
                      <IconChevronLeft />
                      Aula anterior
                    </VideoNavButton>

                    <VideoNavButton
                      type="button"
                      disabled={!nextLesson}
                      onClick={() =>
                        nextLesson && goToLesson(nextLesson.lesson.id)
                      }
                    >
                      Próxima aula
                      <IconChevronRight />
                    </VideoNavButton>
                  </VideoNav>
                </VideoStage>
              </VideoTourLayout>
            </InViewWrapper>
          )}
        </SectionInner>
      </Section>

      {/* ── Seção 5 – Diferenciais ── */}
      <Section
        id="diferenciais"
        $bg="linear-gradient(165deg, #102C56 0%, #081A37 65%)"
      >
        <BrandWatermark
          src={VMCORE_BRAND.iconLight}
          alt=""
          aria-hidden="true"
        />
        <SectionInner>
          <InViewWrapper>
            <SectionLabel>Diferenciais</SectionLabel>
            <SectionTitle>Projetado para quem vive a engenharia.</SectionTitle>
            <SectionSubtitle>
              Capacidades construídas com base na realidade das operações de
              campo.
            </SectionSubtitle>
          </InViewWrapper>

          <DiffGrid>
            {diferenciais.map((d, i) => (
              <InViewWrapper
                key={d.title}
                delay={i % 2}
                style={{ height: '100%' }}
              >
                <DiffItem>
                  <DiffIcon>{d.icon}</DiffIcon>
                  <div>
                    <DiffTitle>{d.title}</DiffTitle>
                    <DiffText>{d.text}</DiffText>
                  </div>
                </DiffItem>
              </InViewWrapper>
            ))}
          </DiffGrid>
        </SectionInner>
      </Section>

      {/* ── Seção 6 – CTA / Formulário ── */}
      <Section
        id="contato"
        $bg="radial-gradient(ellipse 70% 60% at 50% 0%, rgba(0, 86, 237,0.12) 0%, transparent 55%), var(--bg-0)"
      >
        <SectionInner>
          <InViewWrapper>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <SectionLabel className="centered">Fale com a gente</SectionLabel>
              <SectionTitle
                style={{
                  maxWidth: 560,
                  margin: '0 auto 12px',
                }}
              >
                Pronto para centralizar sua operação?
              </SectionTitle>
              <p
                style={{
                  fontSize: 17,
                  lineHeight: 1.65,
                  color: 'var(--texto-dim)',
                  maxWidth: 480,
                  margin: '0 auto',
                }}
              >
                Solicite uma demonstração e veja o VMCORE aplicado à realidade
                da sua empresa.
              </p>
            </div>
          </InViewWrapper>

          <InViewWrapper delay={1}>
            <FormGrid>
              <FormPanel>
                <form onSubmit={handleSubmit} noValidate>
                    <FormFieldsGrid>
                      <FormRow>
                        <FormLabel htmlFor="nome">Nome</FormLabel>
                        <FormInput
                          id="nome"
                          type="text"
                          placeholder="Seu nome completo"
                          value={formState.nome}
                          onChange={handleChange('nome')}
                          aria-invalid={!!formErrors.nome}
                          aria-describedby={
                            formErrors.nome ? 'erro-nome' : undefined
                          }
                        />
                        {formErrors.nome && (
                          <p
                            id="erro-nome"
                            style={{
                              color: '#B42336',
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {formErrors.nome}
                          </p>
                        )}
                      </FormRow>
                      <FormRow>
                        <FormLabel htmlFor="mensagem">Mensagem</FormLabel>
                        <FormTextarea
                          id="mensagem"
                          rows={5}
                          placeholder="Como podemos ajudar?"
                          value={formState.mensagem}
                          onChange={handleChange('mensagem')}
                          aria-invalid={!!formErrors.mensagem}
                          aria-describedby={
                            formErrors.mensagem ? 'erro-mensagem' : undefined
                          }
                        />
                        {formErrors.mensagem && (
                          <p
                            id="erro-mensagem"
                            style={{
                              color: '#B42336',
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {formErrors.mensagem}
                          </p>
                        )}
                      </FormRow>
                    </FormFieldsGrid>
                    <FormSubmitBtn type="submit">
                      Enviar
                    </FormSubmitBtn>
                    <FormNote>
                      O WhatsApp abrirá com a mensagem preenchida para você
                      revisar e enviar.
                    </FormNote>
                  </form>
              </FormPanel>
            </FormGrid>
          </InViewWrapper>
        </SectionInner>
      </Section>

      {/* ── Footer ── */}
      <FooterEl>
        <FooterInner>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <VmcoreLogo height={28} tone="light" animateIcon />
          </div>
          <FooterContacts>
            <a
              href={`https://wa.me/${WHATSAPP_DESTINATION}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaWhatsapp aria-hidden="true" /> WhatsApp
            </a>
            <a
              href="https://www.instagram.com/vmcore.erp/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram aria-hidden="true" /> @vmcore.erp
            </a>
          </FooterContacts>
          <FooterCopy>
            © {new Date().getFullYear()} VMCORE. Todos os direitos reservados.
          </FooterCopy>
        </FooterInner>
      </FooterEl>
    </>
  );
};

VmcoreErpLandingPage.layoutMode = 'none';

export default VmcoreErpLandingPage;
